import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type CarouselItem,
  type CzasotekstOverlay,
  type MediaAsset,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MediaThumb } from "@/components/MediaThumb";
import CaptionChatPanel from "@/components/CaptionChatPanel";
import CzasotekstComposeCanvas, { czasotekstVideoUrl } from "@/components/CzasotekstComposeCanvas";
import { StudioOptionCard } from "@/components/DesignReviewPanel";
import { cn } from "@/lib/utils";

const DEFAULT_OVERLAY: CzasotekstOverlay = {
  frame_id: "pill_dark",
  box: { x: 80, y: 720, w: 920, h: 280, rotate: 0 },
  text: { font: "SourceSans3-Bold", size: 64, color: "#f8f5ef", align: "center" },
};

function fmt(sec: number) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const s = (n - m * 60).toFixed(1);
  return m ? `${m}:${s.padStart(4, "0")}` : `${Number(s).toFixed(1)}s`;
}

export default function CzasotekstPanel({
  item,
  caption,
  onCaption,
  onHook,
}: {
  item: CarouselItem;
  caption: string;
  onCaption: (v: string) => void;
  onHook: (v: string) => void;
}) {
  const qc = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const clip = (item.copy as { czasotekst?: Record<string, unknown> } | undefined)?.czasotekst || {};
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<MediaAsset | null>(null);
  const [inSec, setInSec] = useState(Number(clip.in_sec || 0));
  const [outSec, setOutSec] = useState(Number(clip.out_sec || 6));
  const [scale, setScale] = useState(Number(clip.scale || 1));
  const [panX, setPanX] = useState(Number(clip.x || 0));
  const [panY, setPanY] = useState(Number(clip.y || 0));
  const [loopSec, setLoopSec] = useState(Number(clip.loop_sec || 7));
  const [bedId, setBedId] = useState(String(clip.bed_id || ""));
  const [crossfadeSec, setCrossfadeSec] = useState(Number(clip.audio_crossfade_sec || 1.5));
  const [hook, setHook] = useState(String((item.copy as { hook_on_screen?: string })?.hook_on_screen || item.hook || ""));
  const [composeScreen, setComposeScreen] = useState(false);
  const [overlay, setOverlay] = useState<CzasotekstOverlay>(() => {
    const raw = (clip as { overlay?: CzasotekstOverlay }).overlay;
    return raw?.box ? { ...DEFAULT_OVERLAY, ...raw, box: { ...DEFAULT_OVERLAY.box!, ...raw.box }, text: { ...DEFAULT_OVERLAY.text, ...raw.text } } : DEFAULT_OVERLAY;
  });
  const listQ = useQuery({
    queryKey: ["media-reels", q],
    queryFn: () => api.mediaList({ kind: "video", q }),
  });
  const catalogQ = useQuery({
    queryKey: ["carousel-catalog"],
    queryFn: () => api.carouselCatalog(),
    staleTime: 60_000,
  });
  const reels = useMemo(() => listQ.data?.items || [], [listQ.data]);
  const frames = catalogQ.data?.czasotekst_frames || [];
  const beds = catalogQ.data?.czasotekst_beds || [];
  const ffmpeg = listQ.data?.ffmpeg !== false;
  const activeAssetId = picked?.id || String(clip.asset_id || "");
  const videoUrl = activeAssetId ? czasotekstVideoUrl(activeAssetId) : "";

  const pickAsset = (asset: MediaAsset) => {
    setPicked(asset);
    const dur = Number(asset.duration_sec || 0);
    setInSec(0);
    setOutSec(dur >= 3 ? Math.min(dur, 6) : Math.max(dur, 0.2));
  };

  const applyCopyResult = (data: CarouselItem) => {
    qc.setQueryData(["carousel", item.id], data);
    const nextHook = String((data.copy as { hook_on_screen?: string })?.hook_on_screen || data.hook || "");
    setHook(nextHook);
    onHook(nextHook);
    onCaption(data.caption || "");
  };

  const copyM = useMutation({
    mutationFn: (feedback: string) => api.czasotekstCopy(item.id, feedback),
    onSuccess: applyCopyResult,
  });

  const chatM = useMutation({
    mutationFn: ({ message, apply = false }: { message: string; apply?: boolean }) =>
      api.czasotekstChat(item.id, message, apply),
    onSuccess: applyCopyResult,
  });

  const undoChatM = useMutation({
    mutationFn: () => api.carouselCaptionChatUndo(item.id),
    onSuccess: applyCopyResult,
  });

  const generating = copyM.isPending || item.llm_status === "czasotekst_copy";
  const chatting = chatM.isPending || item.llm_status === "czasotekst_chat";
  const writing = generating || chatting;
  const chatMessages = Array.isArray(item.discussion?.caption_chat) ? item.discussion.caption_chat : [];
  const canUndoChat = Boolean(item.discussion?.caption_chat_undo);
  const pending = item.discussion?.caption_chat_pending || null;
  const pendingHook = String(pending?.hook_on_screen || "").trim();
  const pendingCaption = String(pending?.caption || "").trim();
  const pendingEngage = String(pending?.captionEngage || "").trim();
  const hasPending = Boolean(pendingHook || pendingCaption || pendingEngage);

  useEffect(() => {
    if (writing) return;
    const next = String((item.copy as { hook_on_screen?: string } | undefined)?.hook_on_screen || item.hook || "");
    if (next) setHook(next);
  }, [item.id, item.hook, item.copy, writing]);

  useEffect(() => {
    const raw = ((item.copy as { czasotekst?: { overlay?: CzasotekstOverlay } } | undefined)?.czasotekst || {})
      .overlay;
    if (!raw?.box) return;
    setOverlay({
      ...DEFAULT_OVERLAY,
      ...raw,
      box: { ...DEFAULT_OVERLAY.box!, ...raw.box },
      text: { ...DEFAULT_OVERLAY.text, ...raw.text },
    });
  }, [item.id, item.copy]);

  const clipBody = (compose: boolean) => {
    if (!picked?.id && !clip.asset_id) throw new Error("wybierz film");
    return {
      asset_id: picked?.id || String(clip.asset_id || ""),
      in_sec: inSec,
      out_sec: outSec,
      scale,
      x: panX,
      y: panY,
      loop_sec: loopSec,
      hook_on_screen: hook,
      overlay,
      bed_id: bedId || beds.find((b) => b.default)?.id || beds[0]?.id || "",
      audio_crossfade_sec: crossfadeSec,
      compose,
    };
  };

  const composeM = useMutation({
    mutationFn: () => api.czasotekstCompose(item.id, clipBody(true)),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });

  const saveOverlayM = useMutation({
    mutationFn: () => api.czasotekstCompose(item.id, clipBody(false)),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });

  const saveCaptionM = useMutation({
    mutationFn: () =>
      api.carouselPatchCopy(item.id, {
        caption,
        copy: {
          ...(item.copy || {}),
          hook_on_screen: hook,
          caption,
        },
      }),
    onSuccess: (data) => qc.setQueryData(["carousel", item.id], data),
  });

  const approveM = useMutation({
    mutationFn: () => api.carouselApproveCopy(item.id),
    onSuccess: (data) => {
      applyCopyResult(data);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
      qc.invalidateQueries({ queryKey: ["carousels"] });
    },
  });

  // Szkic z Wejścia: caption bywa pusty, a draft_text już jest. Podpowiedz go w polu opisu.
  useEffect(() => {
    if (caption.trim()) return;
    const draft = String(item.draft_text || "").trim();
    if (draft) onCaption(draft);
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps -- tylko przy nowym itemie

  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-12">
      <div className="min-w-0 space-y-4 xl:col-span-4">
          <div id="czasotekst-text">
            <StudioOptionCard
              optional={false}
              title="Tekst czasotekstu"
              hint="Główna treść jest w opisie do czytania. Na ekranie tylko krótki hak."
            >
              <div className="space-y-2">
                {item.czasotekst_variant_id || item.czasotekst_form_id ? (
                  <p className="text-[11px] text-muted-foreground">
                    {[
                      item.czasotekst_form_id ? `Forma: ${item.czasotekst_form_id}` : "",
                      item.czasotekst_variant_id ? `Hipoteza: ${item.czasotekst_variant_id}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    {" · "}
                    zmiana w Briefie
                  </p>
                ) : null}
                <label className="block text-[11px] text-muted-foreground">Hak na ekranie (max ~12 słów)</label>
                <input
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  value={hook}
                  onChange={(e) => {
                    setHook(e.target.value);
                    onHook(e.target.value);
                  }}
                />
                <label className="block text-[11px] text-muted-foreground">Opis (caption) - to czyta widz</label>
                <textarea
                  className="min-h-[160px] w-full rounded border px-2 py-1.5 text-sm"
                  value={caption}
                  onChange={(e) => onCaption(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="brand" disabled={writing} onClick={() => copyM.mutate("")}>
                    {generating ? "Piszę…" : "Wygeneruj hak + opis"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={saveCaptionM.isPending}
                    onClick={() => saveCaptionM.mutate()}
                  >
                    Zapisz tekst
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!caption.trim() || approveM.isPending}
                    onClick={() => approveM.mutate()}
                  >
                    Zatwierdź tekst
                  </Button>
                </div>
                {copyM.isError ? (
                  <p className="text-xs text-destructive">{(copyM.error as Error)?.message || "błąd"}</p>
                ) : null}
              </div>
            </StudioOptionCard>
          </div>

          <div id="czasotekst-film">
            <StudioOptionCard
              optional={false}
              title="Film i skład ekranu"
              hint="Wybierz fragment, potem ułóż hak na canvasie 9:16. Render wypala ramkę do MP4."
            >
              {!ffmpeg ? (
                <p className="text-xs text-destructive">Na serwerze nie ma ffmpeg. Cięcie nie zadziała.</p>
              ) : null}
              <div className="mb-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={composeScreen ? "outline" : "brand"}
                  onClick={() => setComposeScreen(false)}
                >
                  Film / kadr
                </Button>
                <Button
                  size="sm"
                  variant={composeScreen ? "brand" : "outline"}
                  disabled={!activeAssetId}
                  onClick={() => setComposeScreen(true)}
                >
                  Skład ekranu
                </Button>
              </div>
              {!composeScreen ? (
                <>
              <input
                className="mb-2 w-full rounded border px-2 py-1 text-xs"
                placeholder="szukaj w bibliotece wideo"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <div className="grid max-h-48 grid-cols-3 gap-2 overflow-auto">
                {reels.map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    className={cn(
                      "overflow-hidden rounded border text-left",
                      activeAssetId === asset.id ? "border-brand" : "border-border",
                    )}
                    onClick={() => pickAsset(asset)}
                  >
                    <MediaThumb asset={asset} />
                    <p className="truncate px-1 py-0.5 text-[10px]">{asset.original_name || asset.id.slice(0, 8)}</p>
                  </button>
                ))}
              </div>
              {picked || clip.asset_id ? (
                <div className="mt-3 space-y-2">
                  {picked ? (
                    <video
                      ref={videoRef}
                      key={picked.id}
                      className="max-h-64 w-full rounded border bg-black object-contain"
                      src={api.mediaFile(picked.id, false)}
                      controls
                      muted
                      playsInline
                    />
                  ) : null}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="text-[11px]">
                      Start {fmt(inSec)}
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0.1, Number(picked?.duration_sec || outSec))}
                        step={0.1}
                        value={inSec}
                        className="w-full"
                        onChange={(e) => setInSec(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Koniec {fmt(outSec)}
                      <input
                        type="range"
                        min={inSec + 0.2}
                        max={Math.max(inSec + 0.2, Number(picked?.duration_sec || outSec + 1))}
                        step={0.1}
                        value={outSec}
                        className="w-full"
                        onChange={(e) => setOutSec(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Zbliżenie {scale.toFixed(2)}×
                      <input
                        type="range"
                        min={1}
                        max={2.5}
                        step={0.05}
                        value={scale}
                        className="w-full"
                        onChange={(e) => setScale(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Pętla {loopSec.toFixed(0)} s
                      <input
                        type="range"
                        min={3}
                        max={15}
                        step={1}
                        value={loopSec}
                        className="w-full"
                        onChange={(e) => setLoopSec(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Pozycja X {panX}
                      <input
                        type="range"
                        min={-400}
                        max={400}
                        step={10}
                        value={panX}
                        className="w-full"
                        onChange={(e) => setPanX(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px]">
                      Pozycja Y {panY}
                      <input
                        type="range"
                        min={-400}
                        max={400}
                        step={10}
                        value={panY}
                        className="w-full"
                        onChange={(e) => setPanY(Number(e.target.value))}
                      />
                    </label>
                    <label className="text-[11px] sm:col-span-2">
                      Muzyka (bez dźwięku z filmu)
                      <select
                        className="mt-0.5 w-full rounded border px-1.5 py-1 text-xs"
                        value={bedId || beds.find((b) => b.default)?.id || beds[0]?.id || ""}
                        onChange={(e) => setBedId(e.target.value)}
                      >
                        {beds.map((bed) => (
                          <option key={bed.id} value={bed.id}>
                            {bed.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-[11px] sm:col-span-2">
                      Płynność pętli muzyki {crossfadeSec.toFixed(1)} s
                      <input
                        type="range"
                        min={0.3}
                        max={3}
                        step={0.1}
                        value={crossfadeSec}
                        className="w-full"
                        onChange={(e) => setCrossfadeSec(Number(e.target.value))}
                      />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!activeAssetId}
                      onClick={() => setComposeScreen(true)}
                    >
                      Ułóż hak na ekranie
                    </Button>
                    <Button
                      size="sm"
                      variant="brand"
                      disabled={composeM.isPending || !ffmpeg}
                      onClick={() => composeM.mutate()}
                    >
                      {composeM.isPending ? "Składam MP4…" : "Złóż czasotekst (MP4)"}
                    </Button>
                  </div>
                  {composeM.isError ? (
                    <p className="text-xs text-destructive">{(composeM.error as Error)?.message || "błąd składu"}</p>
                  ) : null}
                  {item.has_reel ? (
                    <video
                      className="max-h-80 w-full rounded border bg-black object-contain"
                      src={api.carouselReelUrl(item.id)}
                      controls
                      playsInline
                    />
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Wybierz film poniżej albo wgraj w Media → Wideo (albo inbox/rolki).
                </p>
              )}
                </>
              ) : (
                <div className="space-y-3">
                  <CzasotekstComposeCanvas
                    frames={frames}
                    overlay={overlay}
                    hook={hook}
                    videoUrl={videoUrl}
                    videoAsset={picked}
                    scalePan={{ scale, x: panX, y: panY }}
                    onChange={setOverlay}
                    onSave={() => saveOverlayM.mutate()}
                    onCompose={() => composeM.mutate()}
                    saving={saveOverlayM.isPending}
                    composing={composeM.isPending}
                    ffmpegOk={ffmpeg}
                  />
                  {(composeM.isError || saveOverlayM.isError) ? (
                    <p className="text-xs text-destructive">
                      {((composeM.error || saveOverlayM.error) as Error)?.message || "błąd zapisu / składu"}
                    </p>
                  ) : null}
                  {item.has_reel ? (
                    <video
                      className="max-h-80 w-full rounded border bg-black object-contain"
                      src={api.carouselReelUrl(item.id)}
                      controls
                      playsInline
                    />
                  ) : null}
                </div>
              )}
            </StudioOptionCard>
          </div>
      </div>

      <aside id="czasotekst-proposal" className="min-w-0 xl:col-span-4 xl:sticky xl:top-4 xl:self-start">
        <div
          className={cn(
            "flex min-h-[min(28rem,calc(100vh-8rem))] flex-col rounded-md border bg-white p-3 shadow-sm",
            hasPending ? "border-violet-200" : "border-slate-200",
          )}
        >
          <div className="border-b border-border pb-2">
            <p className="text-sm font-medium text-slate-900">Propozycja</p>
            <p className="text-[11px] text-muted-foreground">
              Podgląd zmian z czatu. Do pól po lewej trafia dopiero po Wdrażaj.
            </p>
          </div>
          {hasPending ? (
            <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-auto text-sm">
              {pendingHook ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Hak</p>
                  <p className="mt-1 rounded-md border border-violet-100 bg-violet-50/80 px-2 py-1.5 text-violet-950">
                    {pendingHook}
                  </p>
                </div>
              ) : null}
              {pendingCaption ? (
                <div className="min-h-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Opis</p>
                  <p className="mt-1 whitespace-pre-wrap rounded-md border border-violet-100 bg-violet-50/80 px-2 py-1.5 text-violet-950">
                    {pendingCaption}
                  </p>
                </div>
              ) : null}
              {pendingEngage ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Engage</p>
                  <p className="mt-1 text-xs text-violet-900/80">{pendingEngage}</p>
                </div>
              ) : null}
              {!pendingHook && !pendingCaption ? (
                <p className="text-sm text-muted-foreground">Propozycja bez treści pól.</p>
              ) : null}
              <div className="mt-auto flex flex-wrap gap-2 border-t border-border pt-3">
                <Button
                  size="sm"
                  variant="brand"
                  disabled={writing}
                  onClick={() => chatM.mutate({ message: "wdrażaj", apply: true })}
                >
                  Wdrażaj
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={writing}
                  onClick={() => chatM.mutate({ message: "jeszcze nie" })}
                >
                  Jeszcze nie
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">
              Tu pojawi się propozycja haka lub opisu po wiadomości w czacie. Nic jeszcze nie czeka na akceptację.
            </p>
          )}
        </div>
      </aside>

      <aside id="czasotekst-chat" className="min-w-0 xl:col-span-4 xl:sticky xl:top-4 xl:self-start">
          <CaptionChatPanel
            caption={caption}
            engage={item.caption_engage || ""}
            fbEnding=""
            igEnding=""
            messages={chatMessages}
            verifyCta={false}
            busy={chatting}
            canUndo={canUndoChat}
            disabled={generating}
            error={
              (chatM.isError && (chatM.error as Error)?.message) ||
              (undoChatM.isError && (undoChatM.error as Error)?.message) ||
              ""
            }
            onCaptionChange={onCaption}
            onVerifyCta={() => undefined}
            onSend={(message) => chatM.mutate({ message })}
            onUndo={() => undoChatM.mutate()}
            undoPending={undoChatM.isPending}
            showCorpus={false}
            showVerify={false}
            title="Czat o treści"
            hint="Najpierw propozycja w środkowej kolumnie. Wdrażam dopiero gdy powiesz tak / Wdrażaj."
            placeholder={
              caption.trim() || hook.trim()
                ? "Np. „zostaw opis, hak: Mama rzuca przy stole…” albo „pasuje”."
                : "Najpierw Wygeneruj hak + opis po lewej, albo napisz kąt tutaj."
            }
            busyLabel="Myślę…"
          />
      </aside>
    </div>
  );
}
